@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginemodule

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.example.project.dashboard.ScreenManager
import org.example.project.manager.LumaticaApiSettings
import org.example.project.service.CustomerModuleState
import org.example.project.service.customerModule
import org.example.project.service.installed
import org.example.project.service.installedVersion
import org.example.project.service.purchased
import org.example.project.service.storeVersion
import org.example.project.service.ZustandManager
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*


typealias ModuleState = String

object ModuleStateVal {
    const val AVAILABLE = "AVAILABLE"
    const val PURCHASED = "PURCHASED"
    const val INSTALLED = "INSTALLED"
    const val UPDATE_AVAILABLE = "UPDATE_AVAILABLE"
    const val DISABLED = "DISABLED"
    const val INSTALLING = "INSTALLING"
    const val UNINSTALLING = "UNINSTALLING"
    const val MAINTENANCE = "MAINTENANCE"
    const val BLOCKED = "BLOCKED"
}

const val SHOP_PURCHASED_KEY = "shopPurchased"
const val SHOP_PURCHASED_DATE_KEY = "shopPurchasedDate"
private const val SHOP_INSTALLED_VERSION_KEY = "shopInstalledVersion"
private const val SHOP_STORE_VERSION_KEY = "shopStoreVersion"

typealias ShopModuleMeta = All

fun moduleVersionKey(moduleId: Int, version: String): String = "$moduleId|${version.trim()}"
fun All.moduleVersionKey(): String = moduleVersionKey(modulId, modulReferenz)

val ShopModuleMeta.moduleId: Int get() = modulId
val ShopModuleMeta.title: String get() = modulName.ifBlank { "Modul $modulId" }
val ShopModuleMeta.description: String get() = modulBeschreibung.ifBlank { "Keine Beschreibung hinterlegt." }
val ShopModuleMeta.version: String get() = normalizeModuleVersion(modulReferenz)
val ShopModuleMeta.enabledScreens: List<String> get() = modulScreens.filterValues { it }.keys.sorted()
val ShopModuleMeta.features: List<String> get() = enabledScreens.map(ScreenManager::labelForKey)
val ShopModuleMeta.iconPath: String
    get() {
        val coverAssetId = moduleGalleryAssetIds.firstOrNull { it > 0 }
        return if (coverAssetId != null) moduleAssetUrl(coverAssetId) else ""
    }
val ShopModuleMeta.globalState: Int get() = zustand
val ShopModuleMeta.purchased: Boolean get() = shopPurchased
val ShopModuleMeta.installed: Boolean get() = modulInstalliert
val ShopModuleMeta.installedVersion: String? get() = shopInstalledVersion.ifBlank { null }
val ShopModuleMeta.storeVersion: String? get() = shopStoreVersion.ifBlank { null }

private fun normalizeModuleVersion(version: String): String = version.trim().ifBlank { "0.0.0" }

fun normalizeGlobalModuleState(state: Int): Int = when (state) {
    ZustandManager.Modul.State.AKTIVIERT -> ZustandManager.Modul.State.AKTIVIERT
    else -> ZustandManager.Modul.State.DEAKTIVIERT
}

fun All.normalizeScreens(): All = copy(
    modulScreens = modulScreens
        .filterValues { it }
        .toList()
        .sortedBy { it.first }
        .associate { it.first to it.second },
    zustand = normalizeGlobalModuleState(zustand)
)

fun All.toShopMeta(): ShopModuleMeta {
    val normalized = normalizeScreens()
    return normalized.copy(
        key = normalized.moduleVersionKey(),
        shopPurchased = true,
        updateAvailable = normalized.updateAvailable,
        modulInstalliert = normalized.zustand == ZustandManager.Modul.State.AKTIVIERT
    )
}
fun createShopMeta(
    module: All,
    customerState: CustomerModuleState? = null,
    purchaseByExactVersion: Boolean = false
): ShopModuleMeta {
    val exactVersionPurchased = customerState?.customerModule?.modulReferenz?.trim() == module.modulReferenz.trim()
    val purchased = when {
        customerState == null -> false
        purchaseByExactVersion -> customerState.purchased && exactVersionPurchased
        else -> customerState.purchased
    }
    val installed = customerState?.installed == true
    return module.copy(
        key = module.moduleVersionKey(),
        modulInstalliert = installed,
        shopPurchased = purchased,
        updateAvailable = customerState?.updateAvailable == true,
        shopInstalledVersion = customerState?.installedVersion.orEmpty(),
        shopStoreVersion = customerState?.storeVersion.orEmpty()
    )
}

fun moduleAssetUrl(assetId: Int): String {
    if (assetId <= 0) return ""
    return "${LumaticaApiSettings.getBaseUrl()}/api/moduleditor/asset-file/$assetId"
}

fun absolutizeAssetUrl(path: String): String {
    val raw = path.trim()
    if (raw.isBlank()) return ""
    if (raw.startsWith("http://") || raw.startsWith("https://") || raw.startsWith("data:")) return raw
    val normalized = if (raw.startsWith("/")) raw else "/$raw"
    return "${LumaticaApiSettings.getBaseUrl()}$normalized"
}

private fun statusText(state: ModuleState): String = when (state) {
    ModuleStateVal.AVAILABLE -> "Verfuegbar"
    ModuleStateVal.PURCHASED -> "Gekauft"
    ModuleStateVal.INSTALLED -> "Installiert"
    ModuleStateVal.UPDATE_AVAILABLE -> "Update verfuegbar"
    ModuleStateVal.DISABLED -> "Deaktiviert"
    ModuleStateVal.INSTALLING -> "Installiere"
    ModuleStateVal.UNINSTALLING -> "Deinstalliere"
    ModuleStateVal.MAINTENANCE -> "Wartung"
    ModuleStateVal.BLOCKED -> "Gesperrt"
    else -> state
}

private fun statusColor(state: ModuleState): CSSColorValue = when (state) {
    ModuleStateVal.INSTALLED -> Color("#66BB6A")
    ModuleStateVal.UPDATE_AVAILABLE -> Color("#8fd2ff")
    ModuleStateVal.PURCHASED -> Color("#d4c27c")
    ModuleStateVal.MAINTENANCE, ModuleStateVal.BLOCKED -> Color("#ff8da1")
    else -> Color("#aeb7c6")
}

private fun primaryLabel(state: ModuleState): String = when (state) {
    ModuleStateVal.AVAILABLE -> "Aktivieren"
    ModuleStateVal.PURCHASED -> "Installieren"
    ModuleStateVal.DISABLED -> "Aktivieren"
    ModuleStateVal.INSTALLED -> "Deaktivieren"
    ModuleStateVal.UPDATE_AVAILABLE -> "Aktualisieren"
    ModuleStateVal.MAINTENANCE -> "Wartung"
    ModuleStateVal.BLOCKED -> "Gesperrt"
    else -> statusText(state)
}

private fun shopActionLabel(state: ModuleState): String = when (state) {
    ModuleStateVal.AVAILABLE -> "Hinzufügen"
    ModuleStateVal.PURCHASED -> "Gekauft"
    ModuleStateVal.INSTALLED -> "Deaktivieren"
    ModuleStateVal.UPDATE_AVAILABLE -> "Aktualisieren"
    ModuleStateVal.MAINTENANCE -> "Wartung"
    ModuleStateVal.BLOCKED -> "Gesperrt"
    else -> statusText(state)
}

object ModuleCardStyles : StyleSheet() {
    val updateBadgeBlink by keyframes {
        from { opacity(1) }
        50.percent { opacity(0.45) }
        to { opacity(1) }
    }
}

@Composable
fun ModuleLandscapeCard(
    module: ShopModuleMeta,
    state: ModuleState,
    progress: Int,
    onInstall: () -> Unit,
    onActivate: () -> Unit,
    onDeactivate: () -> Unit,
    onUpdate: () -> Unit,
    onDetails: () -> Unit,
    detailsLabel: String,
    primaryShowsStateLabel: Boolean = false,
    primaryStateText: String? = null,
    primaryLabelOverride: String? = null,
    showStatusHint: Boolean = true,
    selected: Boolean = false,
    useShopOverlayLayout: Boolean = false,
    useStateBorder: Boolean = false,
    showOwnershipStagePanel: Boolean = false,
    showPurchaseOnly: Boolean = false,
    showDetailsAction: Boolean = true,
    onSelect: ((Boolean) -> Unit)? = null
) {
    Style(ModuleCardStyles)

    val moduleTypeLabel = module.modulKategorie.trim()
    val borderColor = when {
        selected -> Color("#8fd2ff")
        useStateBorder && state == ModuleStateVal.INSTALLED -> Color("rgba(102,187,106,0.72)")
        useStateBorder && state == ModuleStateVal.UPDATE_AVAILABLE -> Color("rgba(143,210,255,0.72)")
        useStateBorder && state == ModuleStateVal.PURCHASED -> Color("rgba(212,194,124,0.68)")
        else -> Color("rgba(255,255,255,0.10)")
    }

    Div({
        style {
            width(100.percent)
            minHeight(360.px)
            borderRadius(24.px)
            overflow("hidden")
            property("border", "1px solid $borderColor")
            property("box-shadow", if (selected) "0 28px 64px rgba(0,0,0,0.34)" else "0 20px 50px rgba(0,0,0,0.24)")
            backgroundColor(Color("rgba(12,15,20,0.96)"))
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Row)
            cursor(if (onSelect != null) "pointer" else "default")
            property("transition", "box-shadow 0.16s ease, border-color 0.16s ease")
        }
        onClick { event ->
            val multi = (event.nativeEvent.asDynamic().ctrlKey as? Boolean == true) ||
                (event.nativeEvent.asDynamic().metaKey as? Boolean == true)
            onSelect?.invoke(multi)
        }
    }) {
        Div({
            style {
                width(if (useShopOverlayLayout) 58.percent else 68.percent)
                minHeight(360.px)
                property(
                    "background",
                    if (module.iconPath.isNotBlank()) {
                        "linear-gradient(115deg, rgba(91,155,255,0.20) 0%, rgba(15,19,26,0.88) 62%), url('${module.iconPath}') center/cover no-repeat"
                    } else {
                        UISettings.BLUE_SURFACE_GRADIENT
                    }
                )
                position(Position.Relative)
                flexShrink(0)
            }
        }) {
            Span({
                style {
                    position(Position.Absolute)
                    left(18.px)
                    top(18.px)
                    padding(6.px, 12.px)
                    borderRadius(999.px)
                    fontSize(11.px)
                    fontWeight("700")
                    letterSpacing(0.8.px)
                    backgroundColor(Color("rgba(8,10,14,0.40)"))
                    color(Color("#f0f4fb"))
                    property("backdrop-filter", "blur(10px)")
                }
            }) { Text(moduleTypeLabel) }

            Div({
                style {
                    position(Position.Absolute)
                    left(50.percent)
                    bottom(18.px)
                    property("transform", "translateX(-50%)")
                    width(82.percent)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                Span({ style { color(Color("#eef2f9")); fontSize(12.px); property("text-shadow", "0 2px 8px rgba(0,0,0,0.55)") } }) { Text("Version ${module.version}") }
                Span({ style { color(Color("#eef2f9")); fontSize(12.px); property("text-shadow", "0 2px 8px rgba(0,0,0,0.55)") } }) { Text("Screens: ${module.enabledScreens.size}") }
            }

            if (!showPurchaseOnly && state == ModuleStateVal.UPDATE_AVAILABLE) {
                Span({
                    style {
                        position(Position.Absolute)
                        right(18.px)
                        top(18.px)
                        padding(6.px, 12.px)
                        borderRadius(999.px)
                        fontSize(11.px)
                        fontWeight("700")
                        backgroundColor(Color("rgba(8,10,14,0.34)"))
                        color(Color("#f0f4fb"))
                        property("backdrop-filter", "blur(10px)")
                        if (showOwnershipStagePanel) property("animation", "${ModuleCardStyles.updateBadgeBlink.name} 1.05s ease-in-out infinite")
                    }
                }) { Text("Update") }
            }
        }

        Div({
            style {
                width(if (useShopOverlayLayout) 42.percent else 32.percent)
                minWidth(0.px)
                minHeight(360.px)
                padding(22.px, 22.px, 18.px, 22.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                justifyContent(JustifyContent.SpaceBetween)
                gap(14.px)
                backgroundColor(Color("rgba(10,13,18,0.90)"))
                property("border-left", "1px solid rgba(255,255,255,0.08)")
                boxSizing("border-box")
                flexGrow(1)
            }
        }) {
            if (useShopOverlayLayout) {
                val ownershipLabel = when {
                    showPurchaseOnly -> null
                    state == ModuleStateVal.PURCHASED -> "Gekauft"
                    state == ModuleStateVal.INSTALLED -> "Installiert"
                    else -> null
                }
                val actionLabel = when {
                    showPurchaseOnly && state != ModuleStateVal.AVAILABLE -> ZustandManager.ModulShop.textFor(ZustandManager.ModulShop.GEKAUFT)
                    showPurchaseOnly -> "Hinzufügen"
                    showOwnershipStagePanel && state == ModuleStateVal.PURCHASED -> ZustandManager.MeineModule.textFor(ZustandManager.MeineModule.INSTALLIEREN)
                    showOwnershipStagePanel && state == ModuleStateVal.INSTALLED -> ZustandManager.MeineModule.textFor(ZustandManager.MeineModule.DEINSTALLIEREN)
                    showOwnershipStagePanel && state == ModuleStateVal.UPDATE_AVAILABLE -> "Aktualisieren"
                    showOwnershipStagePanel -> primaryLabel(state)
                    else -> shopActionLabel(state)
                }
                val actionEnabled = when {
                    showPurchaseOnly -> state == ModuleStateVal.AVAILABLE
                    showOwnershipStagePanel -> state == ModuleStateVal.PURCHASED || state == ModuleStateVal.INSTALLED || state == ModuleStateVal.UPDATE_AVAILABLE
                    else -> state == ModuleStateVal.AVAILABLE || state == ModuleStateVal.UPDATE_AVAILABLE
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Stretch)
                        height(100.percent)
                        minWidth(0.px)
                        gap(14.px)
                    }
                }) {
                    Span({
                        style {
                            color(Color("#f5f6f8"))
                            fontSize(21.px)
                            fontWeight("700")
                            lineHeight("1.12")
                            property("display", "-webkit-box")
                            property("-webkit-line-clamp", "2")
                            property("-webkit-box-orient", "vertical")
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("word-break", "break-word")
                            textAlign("center")
                        }
                    }) { Text(module.title) }

                    Div({
                        style {
                            flexGrow(1)
                            flexShrink(1)
                            flexBasis(0.px)
                            minHeight(0.px)
                            height(148.px)
                            maxHeight(148.px)
                            overflow("hidden")
                        }
                    }) {
                        Span({
                            style {
                                color(Color("#aeb7c6"))
                                fontSize(14.px)
                                lineHeight("1.58")
                                textAlign("center")
                                display(DisplayStyle.Block)
                                height(100.percent)
                                maxHeight(100.percent)
                                overflow("hidden")
                                property("display", "-webkit-box")
                                property("-webkit-line-clamp", "6")
                                property("-webkit-box-orient", "vertical")
                                property("text-overflow", "ellipsis")
                                property("word-break", "break-word")
                            }
                        }) { Text(module.description) }
                    }

                    if (showDetailsAction) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.Center)
                            }
                        }) {
                            Span({
                                style {
                                    color(UISettings.ACCENT)
                                    fontSize(13.px)
                                    fontWeight("700")
                                    cursor("pointer")
                                    property("width", "fit-content")
                                    paddingBottom(2.px)
                                    property("border-bottom", "2px solid ${UISettings.ACCENT}")
                                    textAlign("center")
                                }
                                onClick { onDetails() }
                            }) { Text(detailsLabel) }
                        }
                    }

                    Div({
                        style {
                            width(100.percent)
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.SpaceBetween)
                            alignItems(AlignItems.Center)
                            gap(12.px)
                            minHeight(40.px)
                        }
                    }) {
                        if (ownershipLabel != null) {
                            Span({
                                style {
                                    color(if (ownershipLabel == "Installiert") Color("#66BB6A") else Color("#d4c27c"))
                                    fontSize(12.px)
                                    fontWeight("700")
                                    property("white-space", "nowrap")
                                }
                            }) { Text(ownershipLabel) }
                        } else {
                            Span({ style { opacity(0) } }) { Text("Status") }
                        }

                        Div({ style { width(188.px); flexShrink(0) } }) {
                            LumaticaButton(
                                label = actionLabel,
                                onClick = {
                                    when {
                                        showPurchaseOnly && state == ModuleStateVal.AVAILABLE -> onInstall()
                                        showOwnershipStagePanel && state == ModuleStateVal.PURCHASED -> onInstall()
                                        showOwnershipStagePanel && state == ModuleStateVal.INSTALLED -> onDeactivate()
                                        showOwnershipStagePanel && state == ModuleStateVal.UPDATE_AVAILABLE -> onUpdate()
                                        !showPurchaseOnly && state == ModuleStateVal.AVAILABLE -> onInstall()
                                        !showPurchaseOnly && state == ModuleStateVal.INSTALLED -> onDeactivate()
                                        !showPurchaseOnly && state == ModuleStateVal.UPDATE_AVAILABLE -> onUpdate()
                                    }
                                },
                                variant = LumaticaButtonVariant.Action,
                                enabled = actionEnabled,
                                heightPx = 40
                            )
                        }
                    }
                }
            } else {
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(10.px); minWidth(0.px) } }) {
                    Span({ style { color(Color("#f5f6f8")); fontSize(22.px); fontWeight("700"); lineHeight("1.1"); property("word-break", "break-word") } }) { Text(module.title) }
                    Span({ style { color(Color("#aeb7c6")); fontSize(14.px); lineHeight("1.55"); property("display", "-webkit-box"); property("-webkit-line-clamp", "5"); property("-webkit-box-orient", "vertical"); property("overflow", "hidden"); property("text-overflow", "ellipsis") } }) { Text(module.description) }
                    if (state == ModuleStateVal.INSTALLING || state == ModuleStateVal.UNINSTALLING) {
                        Div({ style { marginTop(4.px); height(8.px); borderRadius(999.px); backgroundColor(Color.transparent); property("border", "1px solid #2a2d34"); overflow("hidden") } }) {
                            Div({ style { height(100.percent); width(progress.percent); backgroundColor(if (state == ModuleStateVal.UNINSTALLING) Color("#EF5350") else Color("#5b9bff")); property("transition", "width 0.12s linear") } })
                        }
                        Span({ style { color(if (state == ModuleStateVal.UNINSTALLING) Color("#ff8da1") else Color("#8fd2ff")); fontSize(12.px) } }) { Text(if (state == ModuleStateVal.UNINSTALLING) "Deinstalliere Modul... $progress%" else "Installiere Modul... $progress%") }
                    }
                }
                Div({ style { display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px) } }) {
                    Div({ style { display(DisplayStyle.Flex); gap(8.px); flexWrap(FlexWrap.Wrap) } }) {
                        Div({ style { flex(1) } }) {
                            LumaticaButton(
                                label = primaryLabelOverride ?: if (primaryShowsStateLabel) (primaryStateText ?: statusText(state)) else primaryLabel(state),
                                onClick = {
                                    when (state) {
                                        ModuleStateVal.AVAILABLE -> onInstall()
                                        ModuleStateVal.PURCHASED -> onActivate()
                                        ModuleStateVal.DISABLED -> onActivate()
                                        ModuleStateVal.INSTALLED -> onDeactivate()
                                        ModuleStateVal.UPDATE_AVAILABLE -> onUpdate()
                                    }
                                },
                                variant = LumaticaButtonVariant.Action,
                                enabled = !primaryShowsStateLabel && state != ModuleStateVal.INSTALLING && state != ModuleStateVal.UNINSTALLING && state != ModuleStateVal.MAINTENANCE && state != ModuleStateVal.BLOCKED,
                                heightPx = 40
                            )
                        }
                        if (detailsLabel.isNotBlank()) {
                            Div({ style { flex(1) } }) { LumaticaButton(label = detailsLabel, onClick = { onDetails() }, variant = LumaticaButtonVariant.Standard, heightPx = 40) }
                        }
                    }
                    if (showStatusHint) {
                        Span({ style { color(statusColor(state)); fontSize(12.px); fontWeight("600") } }) { Text(statusText(state)) }
                    }
                }
            }
        }
    }
}

@Composable
fun ModuleDetailModal(
    module: ShopModuleMeta,
    state: ModuleState,
    onClose: () -> Unit,
    onEdit: (() -> Unit)? = null
) {
    ShopModuleDetails(
        module = module,
        state = state,
        onClose = onClose,
        onInstall = {},
        onActivate = {},
        onDeactivate = {},
        onUpdate = {},
        readOnly = true
    )
}
